/**
 * The paths whose URL *is* a credential: a password-reset or
 * email-verification token sits in the path itself, so the URL alone is
 * enough to take over the account it belongs to.
 *
 * Five routes qualify — `reset-password/$token` on both sides,
 * `verify-email/…/$token` on both sides, and
 * `verify-work-email/…/$token` — and no analytics destination may be
 * handed one. A `page_location` reaching Google Ads, HubSpot, LinkedIn or
 * Zoho PageSense puts a working credential in a third party's reports.
 *
 * The pattern mirrors the GTM container's own blocking trigger, a Page
 * Path regex, so the guards on both sides of the container agree about
 * which URLs are protected. The trailing slash is the whole point of it:
 * `/reset-password` is the form that asks for an email address and stays
 * measurable, while `/reset-password/<token>` carries the secret.
 *
 * Matches a bare pathname or one carrying the app's `/next` prefix — the
 * router reports the path without it, `window.location.pathname` with it —
 * and a full URL, since the pattern is a substring match either way.
 */
const CREDENTIAL_BEARING_PATH_PATTERN =
  /\/(?:reset-password|verify-email|verify-work-email)\//;

export function isCredentialBearingPath(pathname: string): boolean {
  return CREDENTIAL_BEARING_PATH_PATTERN.test(pathname);
}

/**
 * The safe form of a URL that may carry a token: its origin, which is what
 * a browser itself sends as the referrer under `Referrer-Policy:
 * strict-origin`. A URL that carries no token is returned unchanged —
 * referrer analytics everywhere else is untouched.
 *
 * `undefined` means there is nothing safe to report: the value names a
 * credential-bearing path but no origin to truncate it to. Callers drop
 * the field. Both of the browser sources this runs on
 * (`window.location.href`, `document.referrer`) are absolute by
 * specification, so that answer is reachable only from a value a call
 * site built itself.
 */
export function redactCredentialBearingUrl(url: string): string | undefined {
  if (!isCredentialBearingPath(url)) return url;
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}
